import { Capacitor } from '@capacitor/core';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import {
  Box,
  Button,
  FormControl,
  FormGroup,
  Input,
  MessageBlock,
  PrimaryTheme,
  Textarea,
} from '@deckee/deck-hand';
import { FieldArray, Formik } from 'formik';
import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import * as yup from 'yup';
import { PERMISSIONS_REQUIRED_MESSAGE } from '../../../../../configurations/incident-report';
import { updateIncidentVideos } from '../../../../../services/store/modules/incident-report';
import { VideoUpload } from '../../../../../services/store/modules/incident-report/models';
import useCheckImagesPermissions from '../../../../hooks/use-check-images-permissions';
import PhotoInput from '../../../../shared/PhotoInput/PhotoInput';
import PreviewImage from '../../../../shared/PreviewImage/PreviewImage';
import PreviewVideo from '../../../../shared/PreviewVideo/PreviewVideo';
import { ReactSvg, circlePlay } from '../../../../shared/vector-images';
import useVideoUpload from '../../hooks/useVideoUpload';
import Form from '../Form/Form';

const IncidentDescriptionFormSchema = yup.object({
  vesselRegistration: yup.string().trim().nullable(),
  description: yup.string().trim(),
  imageUrls: yup.array().of(yup.string().required()).default([]),
  videoUploads: yup.array().of(yup.object().required()).default([]),
});

const StyledPhotoInput = styled(PhotoInput)`
  flex: 1 1 0px;
`;

const groupByKey = (list: any[], key: string) =>
  list.reduce(
    (hash, obj) => ({
      ...hash,
      [obj[key]]: obj,
    }),
    {},
  );

const IncidentDescriptionForm: React.FC<{
  onSubmit: (x: any) => any;
  onVideoSelect: ({ uploadId, url }: VideoUpload) => Promise<void>;
  hydratedIncidentDetails?: { [key: string]: any };
}> = (props) => {
  const dispatch = useDispatch();
  const { showPermissionMessage } = useCheckImagesPermissions();
  const { onSubmit, hydratedIncidentDetails, onVideoSelect } = props;
  const initialValues = {
    vesselRegistration: hydratedIncidentDetails?.vesselRegistration ?? '',
    description: hydratedIncidentDetails?.description ?? '',
    imageUrls: hydratedIncidentDetails?.imageUrls ?? [],
    videoUploads: hydratedIncidentDetails?.videoUploads ?? [],
  };

  const { upload, videoUploads, removeVideoByUploadId, uploadInProgress } =
    useVideoUpload({
      initialVideoUploads: groupByKey(initialValues.videoUploads, 'uploadId'),
    });

  const handleUpdateVideos = (uploadId: string) => {
    // updating component state
    removeVideoByUploadId(uploadId);

    // updating store state
    const newVideoUploads = initialValues.videoUploads
      ? initialValues.videoUploads.filter(
          (x: { uploadId: string }) => x.uploadId !== uploadId,
        )
      : [];
    dispatch(updateIncidentVideos(newVideoUploads));
  };

  const handleSubmit = (formValues: any) => {
    const newFormValues = {
      ...formValues,
      videoUploads,
    };
    // using the yup schema cast to trim form values where needed
    onSubmit(IncidentDescriptionFormSchema.cast(newFormValues));
  };

  const handleSubmitVideo = async () => {
    const result = await FilePicker.pickVideos({
      readData: true,
      skipTranscoding: true,
    });
    const { files } = result;

    const firstFile = files[0];

    const buff = Buffer.from(firstFile.data || '', 'base64');
    const x = new File([buff], firstFile.path || firstFile.name, {
      type: firstFile.mimeType,
    });
    try {
      if (firstFile) {
        const videoUpload = await upload([x]);
        onVideoSelect(videoUpload);
      }
    } catch (e) {
      // TODO: What how to handle errors?
    }
  };

  const videoPickerAvailable = Capacitor.isPluginAvailable('FilePicker');

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={IncidentDescriptionFormSchema}
      onSubmit={handleSubmit}
    >
      {({
        handleSubmit: formikHandleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
      }) => (
        <Form onSubmit={formikHandleSubmit}>
          <FormGroup heading="Provide any further details">
            {showPermissionMessage && (
              <MessageBlock type="warning">
                {PERMISSIONS_REQUIRED_MESSAGE}
              </MessageBlock>
            )}

            <FormControl
              inputMaxWidth="300px"
              label="Vessel registration"
              name="vesselRegistration"
              error={touched.vesselRegistration && errors.vesselRegistration}
            >
              <Input
                value={values.vesselRegistration}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormControl>

            <FormControl
              label="Description"
              name="description"
              error={touched.description && errors.description}
            >
              <Textarea
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={8}
              />
            </FormControl>
            <FormControl>
              <FieldArray name="imageUrls">
                {({ remove, push }) => (
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb="2">
                      <StyledPhotoInput
                        fixedButton
                        onPhotoSelect={(imageData: {
                          [key: string]: unknown;
                        }) => {
                          push(imageData);
                        }}
                      />
                      {videoPickerAvailable && (
                        <Button
                          flex="1"
                          ml="2"
                          variant="dashed"
                          type="button"
                          onClick={handleSubmitVideo}
                        >
                          <ReactSvg
                            toDisplay={circlePlay()}
                            options={{
                              fillColor: PrimaryTheme.colors.primary,
                            }}
                            width={22}
                            height={22}
                          />
                          <Box ml="2">Add Video</Box>
                        </Button>
                      )}
                    </Box>
                    <Box display="flex" flexWrap="wrap">
                      {values?.imageUrls.map((photo: string, index: number) => (
                        <PreviewImage
                          key={photo}
                          imageSrc={photo}
                          onClose={() => remove(index)}
                        />
                      ))}
                      {/* When an upload is in progress it will show the component internal state
                      with the upload progress of each file. Otherwise the array from the store */}
                      {(uploadInProgress
                        ? Object.values(videoUploads)
                        : initialValues.videoUploads
                      ).map((video: any) => (
                        <PreviewVideo
                          key={video.uploadId}
                          progress={video.progress}
                          videoSrc={video.localFilePath || ''}
                          onClose={() => handleUpdateVideos(video.uploadId)}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </FieldArray>
            </FormControl>
          </FormGroup>
          <FormGroup mt="auto">
            <Button.Group my={3} wide width="100%">
              <Button disabled={uploadInProgress} type="submit">
                Next
              </Button>
            </Button.Group>
          </FormGroup>
        </Form>
      )}
    </Formik>
  );
};

IncidentDescriptionForm.defaultProps = {
  hydratedIncidentDetails: undefined,
};

export default IncidentDescriptionForm;
